import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Badge from "src/components/Badge"
import Button from "src/components/Button"
import OverflowMenu from "src/components/OverflowMenu"
import MenuItem from "src/components/OverflowMenu/MenuItem"
import ReloadableWidget from "src/components/ReloadableWidget"
import Table from "src/components/Table"

import { queryInvoiceTxns } from "src/api/Payments"

import useHover from "src/hooks/use_hover"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { statusBadgeColor } from "src/utils/status_badge_color_helper"

import PaymentRow from "./PaymentRow"

const HoverableTableRow = ({ invoice }) => {
  const { presentmentCurrency } = useMarina()
  const [expanded, setExpanded] = useState(false)

  const { data, isLoading, isError } = useQuery(
    ["invoiceTxns", invoice.id],
    () => queryInvoiceTxns(invoice.invoiceTxnUrl),
    { retry: false, enabled: expanded }
  )

  const renderAngleIcon = () =>
    expanded ? (
      <i className="fa-solid fa-chevron-up mt-0.5 text-xs" />
    ) : (
      <i className="fa-solid fa-chevron-down mt-0.5 text-xs" />
    )

  const handleViewInvoiceClick = (event) => {
    event.preventDefault()
    event.stopPropagation()

    window.location.href = invoice.sourcePath
  }

  const handleMessageClick = () => {
    window.location.href = invoice.messagePath
  }

  const showOverflowMenu = !!invoice.messagePath

  const renderActions = () => (
    <div className="flex items-center">
      <div className="mr-2">
        {isHovered && <Button onClick={handleViewInvoiceClick}>View</Button>}
      </div>
      {showOverflowMenu && (
        <OverflowMenu variant="tableRow">
          <MenuItem label="Message boater" onClick={handleMessageClick} />
        </OverflowMenu>
      )}
    </div>
  )

  const renderPaymentRows = () => {
    if (expanded) {
      if (isLoading || isError) {
        return (
          <Table.Row>
            <td colSpan="6">
              <ReloadableWidget
                isLoading={isLoading}
                isError={isError}
                border={false}
              />
            </td>
          </Table.Row>
        )
      } else {
        return data.map((payment) => (
          <PaymentRow payment={payment} key={payment.id} />
        ))
      }
    }
  }

  const handleRowClick = () => {
    if (invoice.txnsCount > 0) {
      setExpanded(!expanded)
    }
  }

  const [hoverRef, isHovered] = useHover()
  return (
    <>
      <Table.Row
        key={invoice.displayId}
        variant="parent"
        innerRef={hoverRef}
        onClick={handleRowClick}
      >
        <Table.Cell subtitle={invoice.contactName}>
          <div className="flex items-center">
            {invoice.txnsCount > 0 && renderAngleIcon()}
            <div
              className={classNames("flex flex-col overflow-hidden", {
                "ml-3": invoice.txnsCount > 0,
                "ml-6": invoice.txnsCount === 0,
              })}
            >
              <span className="overflow-hidden truncate">
                {invoice.displayId}
              </span>
              <span className="text-xs text-gray-600">
                {invoice.contactName}
              </span>
            </div>
          </div>
        </Table.Cell>
        <Table.Cell>
          <Badge
            text={invoice.settledStatus}
            color={statusBadgeColor(invoice.settledStatus)}
          />
        </Table.Cell>
        <Table.Cell title={invoice.dueDate} />
        <Table.Cell title={invoice.settledAt} />
        <Table.Cell />
        <Table.Cell>
          <div className="flex w-full items-center justify-between">
            {invoice.totalAmount &&
              formatCurrencyFromMinorUnits({
                amount: invoice.totalAmount,
                currency: presentmentCurrency,
              })}
            {renderActions()}
          </div>
        </Table.Cell>
      </Table.Row>
      {renderPaymentRows()}
    </>
  )
}

HoverableTableRow.propTypes = {
  invoice: PropTypes.shape({
    contactName: PropTypes.string,
    displayId: PropTypes.string,
    id: PropTypes.string,
    invoiceTxnUrl: PropTypes.string,
    settledStatus: PropTypes.oneOf([
      "Settled",
      "Succeeded",
      "Refund Pending",
      "Refunded",
      "Processing",
      "Pending",
      "Failed",
      "Unpaid",
      "Overdue",
      "Past Due",
      "Due Today",
      "Scheduled",
    ]),
    dueDate: PropTypes.string,
    recordType: PropTypes.string,
    sourcePath: PropTypes.string,
    messagePath: PropTypes.string,
    settledAt: PropTypes.string,
    totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    txnsCount: PropTypes.number,
  }),
}

export default HoverableTableRow
